<template>
  <div class="dataAssetValueSummary">
    <div class="header">
      <div class="title">Data Asset/set Value Summary (BBA & NSC hub)</div>
      <div class="opera">
        <n-select
          v-model:value="query.dateMonth"
          class="form_item"
          placeholder="Month"
          :options="options.dateMonths"
          @update:value="fetchChart"
        />
        <n-select
          v-model:value="query.primeName"
          class="form_item"
          placeholder="Prime"
          :options="options.primeNames"
          @update:value="fetchChart"
          clearable
        />
        <!-- <PopoverCheck
          v-model:values="query.primeName"
          title="Prime"
          type="layer"
          :options="options.primeNames"
          @update:values="(value: any) =>renderChart"
        >
          <template #icon>
            <svg-icon icon-name="primeSvg" icon-class="primeSvg" />
          </template>
        </PopoverCheck> -->
      </div>
    </div>
    <div class="container">
      <div class="quadrant">
        <n-grid :x-gap="50" :y-gap="50" :cols="2">
          <n-grid-item v-for="(item, index) in itemCost" :key="item.name" class="quadrant-item">
            <div
              class="quadrant-item-content"
              :style="{
                backgroundColor: item.bgcolor,
              }"
            >
              <div
                :class="{
                  bar: true,
                  'is-right': (index + 1) % 2 === 0,
                }"
                :style="{
                  backgroundColor: item.color,
                }"
              ></div>
              <div
                :class="{
                  title: true,
                  'is-right': (index + 1) % 2 === 0,
                }"
              >
                <span>{{ item.name }}:</span>
                <span>{{ item.percentage }}%</span>

                <n-popover trigger="hover">
                  <template #trigger>
                    <SvgIcon class="tips_icon" icon-name="questTips" icon-class="questTips"></SvgIcon>
                  </template>
                  <p class="tips">{{ item.labelTips }}</p>
                </n-popover>
              </div>

              <div class="costs">
                <div class="cost-item">
                  <div class="cost-title">Running Cost</div>
                  <div class="cost-value">{{ item.totalRunningCost }}K</div>
                </div>
                <div class="cost-item storage">
                  <div class="cost-title">Storage Cost</div>
                  <div class="cost-value">{{ item.totalStorageCost }}K</div>
                </div>
              </div>
            </div>
          </n-grid-item>
        </n-grid>
      </div>
      <div class="coordinate">
        <div class="coordinate-x"><CoordinateX /></div>
        <div class="coordinate-y"><CoordinateY /></div>
      </div>
      <div class="chart">
        <v-chart :option="chart" autoresize></v-chart>
      </div>
    </div>
    <div class="footer">
      <div class="title">Data Asset/set Scope *</div>
      <div class="desc">
        <div class="label">BBA room:</div>
        <div class="item">
          layer=sem and BO in(ito, quality, pursup, hr, finance); layer=sem and status=released and BO in(otd, facility)
        </div>
      </div>
      <div class="desc">
        <div class="label">NSC room:</div>
        <div class="item">layer=sem and BO=In-Vehicle; layer=pre and can be consumed and BO=In-Vehicle</div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';

import { NNumberAnimation } from 'naive-ui';

import CoordinateX from './CoordinateX.vue';
import CoordinateY from './CoordinateY.vue';
import { fetchDataAssetValueSummary, fetchValueSummarySelectOption } from '@/api/dataAssetValueSummary';
// import PopoverCheck from '@/components/PopoverCheck.vue';

const query = reactive({
  dateMonth: null,
  primeName: null,
});

const options = reactive({
  dateMonths: [],
  primeNames: [],
});

const totalNum = ref(0);

const itemCost = ref([
  {
    key: 'considerRetired',
    name: 'Consider Retired',
    bgcolor: 'rgba(255, 24, 180, 0.125)',
    color: '#FF18B4',
    totalRunningCost: 0,
    totalStorageCost: 0,
    percentage: 0,
    total: 0,
    labelTips: 'Monthly Usage<10 & Supporting Use Case Number<2 & Not Sync to Global Cost/Data Asset>=¥0.1K',
  },
  {
    key: 'costControl',
    name: 'Cost Control',
    bgcolor: 'rgba(255, 162, 1, 0.125)',
    color: '#FFA201',
    totalRunningCost: 0,
    totalStorageCost: 0,
    percentage: 0,
    total: 0,
    labelTips: 'Supporting Use Case Number>=2 or Monthly Usage>=10 or Sync to Global Cost/Data Asset>=¥0.1K',
  },
  {
    key: 'toBeOptimized',
    name: 'To-be Optimized',
    bgcolor: 'rgba(23, 14, 103, 0.125)',
    color: '#170E67',
    totalRunningCost: 0,
    totalStorageCost: 0,
    percentage: 0,
    total: 0,
    labelTips: 'Monthly Usage<10 & Supporting Use Case Number<2 & Not Sync to Global Cost/Data Asset< ¥0.1K',
  },
  {
    key: 'encourage',
    name: 'Encourage',
    bgcolor: 'rgba(87, 45, 243, 0.125)',
    color: '#572DF3',
    totalRunningCost: 0,
    totalStorageCost: 0,
    percentage: 0,
    total: 0,
    labelTips: 'Supporting Use Case Number>=2 or Monthly Usage>=10 or Sync to Global Cost/Data Asset<¥0.1K',
  },
]);

const renderChartCost = computed(() => {
  /**
   * 1 2
   * 3 4  ---> 2431 顺时针的顺序
   */
  const [first, second, third, forth] = itemCost.value;
  return [second, forth, third, first];
});
const calcSize = ref(parseInt(getComputedStyle(window.document.documentElement)['font-size'], 10));
const fontSize = computed(() => (12 / 192) * calcSize.value);
const itemWidth = computed(() => (12 / 192) * calcSize.value);
const itemHeight = computed(() => (12 / 192) * calcSize.value);
const toolTipSize = computed(() => (14 / 192) * calcSize.value);
const textSize = computed(() => (16 / 192) * calcSize.value);
const chartRef = ref(null);

const colorMap = ['#f9a104', '#600af2', '#1d0366', '#f900b2'];

const chart = computed(() => {
  return {
    tooltip: {
      trigger: 'item',
      confine: true,
      textStyle: {
        fontSize: toolTipSize.value,
        fontFamily: 'BMWTypeNextRegular',
      },
      formatter: (params: any) => {
        console.log(params);
        return `
        <div style='color:#666'>
          <div style='font-size: ${14 / 192}rem;'>${params?.data?.name}:</div>
          <div style='font-size: ${14 / 192}rem;'>${params?.data?.total} data assets/sets { ${
          params?.data?.percentage
        }% } </div>
        </div>
      `;
      },
    },
    color: colorMap,
    series: [
      {
        name: 'Access From',
        type: 'pie',
        radius: [0, '70%'],
        label: {
          position: 'inner',
          fontSize: 14,
        },
        emphasis: {
          disabled: true,
        },
        emptyCircleStyle: {
          color: '#eceefd',
        },
        data: [],
      },
      {
        type: 'pie',
        radius: ['70%', '90%'],
        avoidLabelOverlap: false,
        stillShowZeroSum: false,
        label: {
          show: true,
          position: 'center',
          width: '120',
          overflow: 'break',
          formatter: () => {
            return `{a|Data Asset/set \nScope *\n}{b|\n${totalNum.value}}`;
          },
          rich: {
            a: {
              fontSize: 14,
              fontWeight: 400,
              lineHeight: 20,
            },
            b: {
              fontSize: 32,
              fontWeight: 700,
              lineHeight: 50,
            },
          },

          textStyle: {
            color: '#161616',
            fontWeight: 'bold',
            fontSize: textSize.value,
          },
        },
        labelLine: {
          show: false,
        },
        data: renderChartCost.value.map((item) => {
          return {
            ...item,
            value: item.total,
            name: item.name,
          };
        }),
      },
      {
        name: '外层背景',
        type: 'pie',
        color: '#EBEEFD',
        radius: ['90%', '91%'],
        silent: true,
        label: {
          normal: {
            show: false,
          },
        },
        data: [
          {
            value: 0,
            name: '外层背景',
          },
        ],
      },
      {
        name: '111',
        type: 'pie',
        color: '#572DF3',
        radius: ['91%', '95%'],
        silent: true,
        label: {
          normal: {
            show: false,
          },
        },
        itemStyle: {
          opacity: 0.1,
        },
        data: [
          {
            value: 0,
            name: '',
          },
        ],
      },
    ],
  };
});

const getOptions = async () => {
  const data = await fetchValueSummarySelectOption();

  options.dateMonths = data.dateMonths.map((item) => ({ label: item, value: item }));
  options.primeNames = data.primeNames.map((item) => ({ label: item, value: item }));

  query.dateMonth = data.dateMonths[data.dateMonths.length - 1];
};

const fetchChart = async () => {
  const data = await fetchDataAssetValueSummary(query);

  let total = 0;

  itemCost.value.forEach((item) => {
    item.totalRunningCost = data[item.key]?.totalRunningCost;
    item.percentage = data[item.key]?.percentage;
    item.total = data[item.key]?.total;
    item.totalStorageCost = data[item.key]?.totalStorageCost;

    total += data[item.key]?.total;
  });

  totalNum.value = total;
};

onMounted(async () => {
  await getOptions();
  await fetchChart();
});
</script>
<style lang="scss" scoped>
.dataAssetValueSummary {
  height: 100%;
  padding: 30px 80px;
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 50px;
  height: 50px;

  color: #262626;
  font-family: BMWTypeNext, sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;

  .opera {
    display: flex;
    gap: 10px;
    .n-select {
      width: 200px;
    }
  }
}

.container {
  flex: 1;
  padding: 30px 50px;
  position: relative;
  .quadrant {
    width: 100%;
    height: 100%;

    .n-grid {
      height: 100%;
    }

    .quadrant-item {
      height: 100%;

      &-content {
        position: relative;
        height: 100%;
      }

      .bar {
        position: absolute;
        width: 25px;
        height: 100%;
        left: 0;
        &.is-right {
          left: auto;
          right: 0;
        }
      }
      .title {
        color: #262626;
        font-family: BMWTypeNext, sans-serif;
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: 26px; /* 144.444% */
        padding: 24px 48px;

        display: flex;
        align-items: center;
        gap: 10px;

        &.is-right {
          justify-content: flex-end;
        }
      }

      .costs {
        display: flex;
        margin: 50px 0 0 50px;

        .cost-item {
          width: 50%;
          padding-left: 30px;

          &.storage {
            border-left: 1px solid #ceb7db;
          }
        }

        .cost-title {
          color: #262626;
          font-family: BMWTypeNext, sans-serif;
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: 22px; /* 157.143% */
        }

        .cost-value {
          color: #262626;
          font-family: BMWTypeNext, sans-serif;
          font-size: 38px;
          font-style: normal;
          font-weight: 700;
          line-height: 46px; /* 121.053% */
        }
      }
    }
  }

  .coordinate {
    &-x {
      width: calc(100% - 100px);
      position: absolute;
      top: calc(50% - 25px);
      z-index: 2;
    }
    &-y {
      position: absolute;

      height: calc(100% - 60px);
      top: 30px;
      left: calc(50% - 25px);
      z-index: 2;
    }
  }
  .chart {
    width: 300px;
    height: 300px;
    position: absolute;
    top: calc(50% - 150px);
    left: calc(50% - 150px);
    z-index: 100;
  }
}

.footer {
  height: 80px;
  padding: 0 50px;

  .title {
    color: #262626;
    font-family: BMWTypeNext, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 22px; /* 157.143% */
  }
  .desc {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .label {
    color: #262626;
    font-family: BMWTypeNext, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px; /* 157.143% */
  }
  .item {
    color: rgba(0, 0, 0, 0.6);
    font-family: BMWTypeNext, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
  }
}
</style>
